import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Button, List, ListItem, Row } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { getEventSummaryFromField } from '../utils/events.utils'

/**
 * PrimaryEventView
 *
 * Displays an existing primary event in read-only mode as a ListItem (event type as
 * primary text, date/place as secondary text) with a trailing Edit button.
 *
 * This component is purely presentational with no internal state.
 *
 * @param {Object} props
 * @param {Object} props.event - The primary event field data
 * @param {Function} props.onEdit - Callback when the Edit button is clicked
 * @returns {JSX.Element}
 */
const PrimaryEventView = ({ event, onEdit }) => {
  const [t] = useTranslation()
  const eventData = useMemo(() => getEventSummaryFromField(event), [event])

  return (
    <Row alignY="middle">
      <List growX>
        <ListItem primaryText={event?.eventType?.primaryText} secondaryText={eventData || undefined} dense />
      </List>
      <Button
        emphasis="low"
        onClick={onEdit}
        aria-label={t('eventsForm.primary-event-edit-button-label', 'Edit primary event')}
        data-testid="edit-primary-event-button"
      >
        {t('navigation.edit-button', 'Edit')}
      </Button>
    </Row>
  )
}

PrimaryEventView.propTypes = {
  event: PropTypes.shape({
    eventType: PropTypes.shape({
      primaryText: PropTypes.string,
    }),
    // eslint-disable-next-line react/forbid-prop-types
    dates: PropTypes.array,
    // eslint-disable-next-line react/forbid-prop-types
    places: PropTypes.array,
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
}

export default PrimaryEventView
